import {
  OrganizationManager,
  PartnerOrganizationLead,
  VolunteerOrganizationLead,
  type OrganizationManager as OrganizationManagerType,
} from "@entities";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { organizationManagerRepo } from "@repositories";
import type { HyperTableFilter } from "@shared/frontend/hyper-table";
import { HyperTableCustomFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/HyperTableCustomFilter";
import { HyperTableEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/HyperTableEntityFilter";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { normalizePhoneDigitsForSearch } from "@shared/helpers/normalize-phone-for-search";
import { partnerOrgLeadContactChannelLabel } from "@shared/helpers/partner-organization-lead-contact";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import type { ClassType, EntityFilter } from "remult";

type OrganizationLeadEntity = ClassType<any> & {
  hyperFilter: (filter: any) => EntityFilter<any>;
};

function filterBoxSx(minWidth: number) {
  return {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    paddingInline: "0.5rem",
    minWidth,
  } as const;
}

function toValidDate(value: unknown): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isFinite(date.getTime()) ? date : null;
}

function LeadHandlingManagerFilter({
  entity,
  meta,
  onSave,
}: {
  entity: OrganizationLeadEntity;
  meta: Record<string, unknown>;
  onSave: (next: {
    value: EntityFilter<any>;
    meta: Record<string, unknown>;
  }) => void;
}) {
  const [selectedManagers, setSelectedManagers] = useState<
    OrganizationManagerType[]
  >((meta.selectedManagers as OrganizationManagerType[]) ?? []);
  const [options, setOptions] = useState<OrganizationManagerType[]>([]);
  const [search, setSearch] = useState("");

  const searchManagers = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    const phoneQuery = normalizePhoneDigitsForSearch(q);
    const list = await organizationManagerRepo.find({
      where: OrganizationManager.hyperFilter({
        $or: [
          { name: { $contains: q } },
          { phoneNumber: { $contains: phoneQuery } },
        ],
      }),
      include: { organization: true },
      orderBy: { name: "asc" },
      limit: 25,
    });
    setOptions(list);
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void searchManagers(search);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search, searchManagers]);

  return (
    <Box sx={filterBoxSx(280)}>
      <Autocomplete
        multiple
        options={options}
        value={selectedManagers}
        onChange={(_, value) => setSelectedManagers(value)}
        inputValue={search}
        onInputChange={(_, value) => setSearch(value)}
        getOptionLabel={(manager) =>
          `${manager.name}${manager.organization?.name ? ` · ${manager.organization.name}` : ""}`
        }
        isOptionEqualToValue={(a, b) => a.id === b.id}
        noOptionsText="הקלד לפחות 2 תווים"
        renderTags={(value, getTagProps) =>
          value.map((manager, index) => (
            <Chip
              {...getTagProps({ index })}
              key={manager.id}
              label={manager.name}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label="חיפוש מנהל מלווה" size="small" />
        )}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const managerIds = selectedManagers.map((manager) => manager.id);
            onSave({
              value: managerIds.length
                ? entity.hyperFilter({
                    handlingManagerId: { $in: managerIds },
                  })
                : {},
              meta: {
                selectedManagers,
                text: selectedManagers.map((manager) => manager.name).join(", "),
              },
            });
          }}
        >
          שמירה
        </Button>
      </Box>
    </Box>
  );
}

function createHandlingManagerFilter(
  entity: OrganizationLeadEntity,
): HyperTableFilter<any> {
  return {
    id: "handlingManager",
    icon: "literal",
    caption: "מנהל מלווה",
    renderFilter: ({ onSave, meta }) => (
      <LeadHandlingManagerFilter
        entity={entity}
        meta={meta}
        onSave={onSave}
      />
    ),
  };
}

function createPhoneFilter(entity: OrganizationLeadEntity): HyperTableFilter<any> {
  return {
    id: "phoneNumber",
    icon: "string",
    caption: "טלפון",
    renderFilter: ({ onSave, meta }) => {
      const text = (meta.text as string) ?? "";
      return (
        <Box sx={filterBoxSx(220)}>
          <TextField
            size="small"
            label="טלפון"
            defaultValue={text}
            onBlur={(event) => {
              const query = event.target.value.trim();
              const digits = normalizePhoneDigitsForSearch(query);
              const term = digits || query;
              onSave({
                value: term
                  ? entity.hyperFilter({
                      phoneNumber: { $contains: term },
                    })
                  : {},
                meta: { text: query },
              });
            }}
          />
        </Box>
      );
    },
  };
}

function createCreatedAtFilter(
  entity: OrganizationLeadEntity,
): HyperTableFilter<any> {
  return {
    id: "createdAt",
    icon: "date",
    caption: "תאריך יצירה",
    renderFilter: ({ onSave, meta }) => {
      const startDate = toValidDate(meta.startDate);
      const endDate = toValidDate(meta.endDate);

      const buildFilter = (start: Date | null, end: Date | null) => {
        if (!start && !end) return {};
        return entity.hyperFilter({
          createdAt: {
            ...(start ? { $gte: start } : {}),
            ...(end ? { $lte: end } : {}),
          },
        });
      };

      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={
            heIL.components.MuiLocalizationProvider.defaultProps.localeText
          }
          adapterLocale="he"
        >
          <Box sx={{ ...filterBoxSx(280), gap: "1rem" }}>
            <DatePicker
              value={startDate ? dayjs(startDate) : null}
              onChange={(date) => {
                const nextStart = date ? dayjs(date).startOf("day").toDate() : null;
                onSave({
                  value: buildFilter(nextStart, endDate),
                  meta: { startDate: nextStart, endDate },
                });
              }}
              label="מתאריך"
              reduceAnimations
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              value={endDate ? dayjs(endDate) : null}
              onChange={(date) => {
                const nextEnd = date ? dayjs(date).endOf("day").toDate() : null;
                onSave({
                  value: buildFilter(startDate, nextEnd),
                  meta: { startDate, endDate: nextEnd },
                });
              }}
              label="עד תאריך"
              reduceAnimations
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small" } }}
            />
          </Box>
        </LocalizationProvider>
      );
    },
  };
}

export function createOrganizationLeadFilters(
  entity: OrganizationLeadEntity,
): HyperTableFilter<any>[] {
  return [
    { fieldName: "name", id: "name", caption: "שם הארגון" },
    createHandlingManagerFilter(entity),
    createPhoneFilter(entity),
    {
      fieldName: "contactChannel",
      id: "contactChannel",
      caption: "דרך יצירת קשר",
      getOptionCaption: (option: string) =>
        partnerOrgLeadContactChannelLabel(option),
    },
    createCreatedAtFilter(entity),
  ];
}

export const PARTNER_ORGANIZATION_LEAD_FILTERS = createOrganizationLeadFilters(
  PartnerOrganizationLead,
);

export const VOLUNTEER_ORGANIZATION_LEAD_FILTERS =
  createOrganizationLeadFilters(VolunteerOrganizationLead);

export function organizationLeadFilterItems(filters: HyperTableFilter<any>[]) {
  return filters.map((filter) =>
    isEntityFilter(filter) ? (
      <HyperTableEntityFilter key={filter.id} {...(filter as any)} />
    ) : (
      <HyperTableCustomFilter key={filter.id} {...filter} />
    ),
  );
}
